'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { ComponentProps, ReactNode, createContext, useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import { getPriceValueTranslation } from '@/entities/cart';
import { mapOrderItemsToCartAdds } from '@/entities/order/lib/buy-again.utils';
import { itemNameFromMenu, selectionLines, variantNameFromMenu } from '@/entities/order/lib/order-receipt';
import { OrderNumber } from '@/entities/order/ui/order-number';
import { getOrderStatusIcon, orderStateMap } from '@/entities/order/ui/order-progress';
import { useStorefrontItemsIndex } from '@/entities/product/lib/useStorefrontMenu';
import { OrderAddonTree, type AddonLine } from '@/features/order/ui/order-addon-tree';
import { QueryApi } from '@/features/query/api';
import { useGetPageUrl } from '@/schema/useGetPageUrl';
import { useRouter } from '@/shared/i18n/navigation';
import { EventService } from '@/shared/lib/event-service';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { useSelectedCurrency } from '@/shared/lib/hooks/useSelectedCurrency';
import { Country } from '@/shared/model/Country';
import { Currency } from '@/shared/model/Currency';
import { LocalizedString } from '@/shared/model/Language';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalService } from '@/shared/ui/modal/ModalService';
import { ModalProps } from '@/shared/ui/modal/types';
import './order-modal.scss';

type Props = ModalProps & {
    orderId: string;
    children?: ReactNode;
};

type ProcessedItem = {
    id: string;
    name: LocalizedString;
    quantity: number;
    basePrice: number;
    extrasPrice: number;
    priceValue: {
        amount: number;
        currency: Currency;
    };
    packages?: Array<{
        name: LocalizedString;
        price: number;
    }>;
    addons?: AddonLine[];
    note?: string | null;
};

type OrderDto = NonNullable<
    Awaited<ReturnType<NonNullable<ReturnType<typeof QueryApi.Order.queries.Order>['queryFn']>>>
>;

type OrderModalContextValue = {
    orderId: string;
    order: OrderDto;
    processedItems: ProcessedItem[];
    formattedDateTime: string;
    onClose: ModalProps['onClose'];
};

const OrderModalContext = createContext<OrderModalContextValue | null>(null);

const useOrderModalContext = () => {
    const context = useContext(OrderModalContext);
    if (!context) {
        throw new Error('OrderModal components must be used within OrderModal.');
    }
    return context;
};

const OrderModalProvider = ({ children, orderId, ...props }: Props) => {
    const locale = useLocale();
    const orderQuery = useQuery(QueryApi.Order.queries.Order(orderId));
    const itemsLookup = useStorefrontItemsIndex();

    const order = orderQuery.data;

    if (!order) {
        return null;
    }

    const formattedDateTime = new Date(order.createdAt).toLocaleString(locale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
    });

    const menuBodies = Object.values(itemsLookup).map((entry) => entry.body);
    // Process order items with addons
    const processedItems: ProcessedItem[] = order.items.map((orderItem) => {
        const addons: NonNullable<ProcessedItem['addons']> = selectionLines(orderItem.selectedAddons, (row) =>
            variantNameFromMenu(menuBodies, row.itemId, row.selectedVariantId),
        );

        // Determine the item name to display
        let itemName = orderItem.item.name;

        // If a variant was selected, append the variant's name in parentheses.
        // Prefer the immutable snapshot captured at order time
        // (selectedVariant.item.name), falling back to a live menu lookup by
        // variantItemId for older orders without snapshot data.
        if (orderItem.selectedVariant) {
            // The REST order payload carries an immutable variant-name snapshot
            // (`selectedVariant.item.name`) that the SDK DTO type omits.
            const selectedVariant = orderItem.selectedVariant as typeof orderItem.selectedVariant & {
                item?: { name?: LocalizedString | null };
            };
            let variantName: LocalizedString | null = null;
            if (selectedVariant.item?.name) {
                variantName = selectedVariant.item.name;
            } else if (selectedVariant.variantItemId) {
                variantName = itemNameFromMenu(menuBodies, selectedVariant.variantItemId) ?? null;
            }
            if (variantName) {
                const merged: Record<string, string | undefined> = {};
                const baseName = itemName as Record<string, string | undefined>;
                const resolvedVariantName = variantName as Record<string, string | undefined>;
                for (const lang of Object.keys(baseName)) {
                    const variantText = resolvedVariantName[lang];
                    merged[lang] = variantText ? `${baseName[lang]} (${variantText})` : baseName[lang];
                }
                itemName = merged;
            }
        }

        // Calculate total package price
        const packagePrice = orderItem.packages?.reduce((sum, pkg) => sum + Number(pkg.price), 0) ?? 0;

        // Calculate total addon price (including addon packages)
        const addonPrice =
            orderItem.selectedAddons?.reduce((sum, addon) => {
                const quantity = Number(addon.quantity ?? 0);
                const itemPrice = Number(addon.price ?? 0) * quantity;
                const packagesPrice =
                    (addon.packages ?? []).reduce((pkgSum, pkg) => pkgSum + Number(pkg.price ?? 0), 0) *
                    quantity;
                return sum + itemPrice + packagesPrice;
            }, 0) ?? 0;

        const itemPrice = Number(orderItem.price);
        const extrasPrice = packagePrice + addonPrice;

        return {
            id: orderItem.id,
            name: itemName,
            quantity: Number(orderItem.quantity),
            basePrice: itemPrice,
            extrasPrice,
            priceValue: {
                amount: itemPrice + extrasPrice,
                currency: order.currency as Currency,
            },
            packages:
                orderItem.packages?.map((pkg) => ({
                    name: pkg.item.name,
                    price: Number(pkg.price),
                })) ?? undefined,
            addons: addons.length > 0 ? addons : undefined,
            note: orderItem.note,
        };
    });

    return (
        <OrderModalContext.Provider
            value={{
                orderId,
                order,
                processedItems,
                formattedDateTime,
                onClose: props.onClose,
            }}
        >
            {children}
        </OrderModalContext.Provider>
    );
};

/** Renders only the title text. Use inside prefab: "Modal.Title". */
const OrderModalTitle = () => {
    const t = useTranslations();
    return <>{t('ORDER_MODAL.TITLE')}</>;
};

const OrderModalInfo = () => {
    const { order, formattedDateTime } = useOrderModalContext();
    const getLocalizedValue = useGetLocalizedValue();

    const statusIdentifier = order.status?.definition?.identifier;
    const state = statusIdentifier ? orderStateMap[statusIdentifier] : null;
    const statusIconName = useMemo(() => (state ? getOrderStatusIcon(state) : null), [state]);
    const statusName = order.status?.definition?.name
        ? getLocalizedValue(order.status.definition.name)
        : null;

    return (
        <div className="order-modal__info">
            <OrderNumber orderNumber={order.scopedNumber} data-testid="order-modal-order-number" />
            {statusIconName && statusName && (
                <div className="order-modal__status" data-testid="order-modal-status">
                    <Icon
                        name={statusIconName}
                        className="order-modal__status-icon"
                        data-testid="order-modal-status-icon"
                    />
                    <span className="order-modal__status-name" data-testid="order-modal-status-name">
                        {statusName}
                    </span>
                </div>
            )}
            <span data-testid="order-modal-datetime">{formattedDateTime}</span>
        </div>
    );
};

const OrderModalDetails = () => {
    const t = useTranslations();
    const { order, processedItems } = useOrderModalContext();
    const getLocalizedValue = useGetLocalizedValue();

    return (
        <div className="order-modal__details">
            <div className="order-modal__customer" data-testid="order-modal-customer">
                <p className="order-modal__customer-name">
                    {order.personalInfo.name} {order.personalInfo.surname || ''}
                </p>
                {order.contactInfo.telephone && (
                    <div className="order-modal__customer-contact">
                        <Icon name="phone" />
                        <span>
                            {order.contactInfo.telephonePrefix
                                ? `${order.contactInfo.telephonePrefix} ${order.contactInfo.telephone}`
                                : order.contactInfo.telephone}
                        </span>
                    </div>
                )}
                {order.contactInfo.email && (
                    <div className="order-modal__customer-contact">
                        <Icon name="mail" />
                        <span>{order.contactInfo.email}</span>
                    </div>
                )}
            </div>
            <hr />
            {order.note && (
                <>
                    <p className="order-modal__note" data-testid="order-modal-note">
                        {t('ORDER_MODAL.NOTE', { note: order.note })}
                    </p>
                    <hr />
                </>
            )}
            <div className="order-modal__items" data-testid="order-modal-items">
                {processedItems.map((item) => (
                    <div key={item.id} className="order-modal__item">
                        <div className="order-modal__item-header">
                            <div className="order-modal__item-title">
                                <span
                                    className="order-modal__item-name"
                                    data-testid={`order-modal-item-name`}
                                >
                                    {getLocalizedValue(item.name)}
                                </span>
                            </div>
                            <div className="order-modal__item-pricing">
                                <span
                                    className="order-modal__item-unit-price"
                                    data-testid={`order-modal-item-unit-price`}
                                >
                                    {getPriceValueTranslation({
                                        amount: item.basePrice,
                                        currency: item.priceValue.currency,
                                        t,
                                    })}
                                </span>
                                {item.extrasPrice !== 0 && (
                                    <span
                                        className="order-modal__item-extras"
                                        data-testid="order-modal-item-extras"
                                    >
                                        {`+${getPriceValueTranslation({
                                            amount: item.extrasPrice,
                                            currency: item.priceValue.currency,
                                            t,
                                        })}`}
                                    </span>
                                )}
                                <span
                                    className="order-modal__item-quantity"
                                    data-testid={`order-modal-item-quantity`}
                                >
                                    {item.quantity}x
                                </span>
                                <span
                                    className="order-modal__item-total-price"
                                    data-testid={`order-modal-item-total-price`}
                                >
                                    {getPriceValueTranslation({
                                        amount: item.priceValue.amount * item.quantity,
                                        currency: item.priceValue.currency,
                                        t,
                                    })}
                                </span>
                            </div>
                        </div>
                        {(!!item.packages?.length || !!item.addons?.length) && (
                            <div className="order-modal__mods">
                                {item.packages?.map((pkg, pkgIndex) => (
                                    <div key={pkgIndex} className="order-modal__mod-node">
                                        <p className="order-modal__item-package">
                                            <Icon name="package" />
                                            <span data-testid={`order-modal-item-package-${pkgIndex}`}>
                                                {getLocalizedValue(pkg.name)}
                                            </span>
                                            {pkg.price > 0 && (
                                                <span className="order-modal__mod-price order-modal__mod-price--package">
                                                    {`+${getPriceValueTranslation({
                                                        amount: pkg.price,
                                                        currency: order.currency as Currency,
                                                        t,
                                                    })}`}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                ))}
                                {item.addons && item.addons.length > 0 && (
                                    <OrderAddonTree
                                        lines={item.addons}
                                        currency={order.currency as Currency}
                                    />
                                )}
                            </div>
                        )}
                        {item.note && (
                            <div className="order-modal__item-note" data-testid="order-modal-item-note">
                                {t('ORDER_MODAL.ITEM_NOTE', { note: item.note })}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

type BuyAgainButtonProps = ComponentProps<typeof Layout>;
const OrderModalBuyAgainButton = ({ media, ...props }: BuyAgainButtonProps) => {
    const t = useTranslations();
    const { order } = useOrderModalContext();
    const buyAgainItemsLookup = useStorefrontItemsIndex();
    const router = useRouter();
    const getPageUrl = useGetPageUrl();
    const venueQuery = useQuery({
        ...QueryApi.Venue.queries.Venue,
        select: (venue) => venue?.address?.country,
    });
    const [selectedCurrency] = useSelectedCurrency();

    const handleBuyAgain = () => {
        if (!Object.keys(buyAgainItemsLookup).length) return;

        const {
            items: itemsToAdd,
            hasUnavailableItems,
            hasPriceChanges,
        } = mapOrderItemsToCartAdds(order.items, buyAgainItemsLookup, {
            country: (venueQuery.data as Country | undefined) ?? Country.CZ,
            currency: selectedCurrency,
        });

        // Trigger batch add-to-cart event without showing modal
        if (itemsToAdd.length > 0) {
            EventService.trigger('cart:product:add-to-cart', {
                items: itemsToAdd,
                suppressModal: true,
            });

            // Show notification
            if (hasUnavailableItems) {
                toast(({ closeToast }) => (
                    <Alert
                        heading={t('ORDER_MODAL.BUY_AGAIN_PARTIAL_SUCCESS')}
                        type="alert"
                        onClose={closeToast}
                    />
                ));
            } else if (hasPriceChanges) {
                toast(({ closeToast }) => (
                    <Alert
                        heading={t('ORDER_MODAL.BUY_AGAIN_PRICE_CHANGED')}
                        type="alert"
                        onClose={closeToast}
                    />
                ));
            } else {
                toast(({ closeToast }) => (
                    <Alert heading={t('ORDER_MODAL.BUY_AGAIN_SUCCESS')} type="success" onClose={closeToast} />
                ));
            }

            // Close all modals and navigate to cart
            ModalService.hideAll();
            const cartUrl = getPageUrl('cart');
            if (cartUrl) {
                router.push(cartUrl);
            }
        } else {
            // All items unavailable
            toast(({ closeToast }) => (
                <Alert heading={t('ORDER_MODAL.BUY_AGAIN_ERROR')} type="error" onClose={closeToast} />
            ));
        }
    };

    return (
        <Layout
            element={Button}
            media={media}
            data-testid="order-modal-buy-again-button"
            onClick={handleBuyAgain}
            {...props}
        >
            {t('ORDER_MODAL.BUY_AGAIN_BUTTON')}
        </Layout>
    );
};

export const OrderModalWrapper = ({ children, ...props }: Props) => {
    return (
        <OrderModalProvider {...props}>
            <Modal
                {...props}
                wrapperProps={{
                    className: 'order-modal',
                    containerProps: {
                        className: 'order-modal__container',
                    },
                    'data-testid': 'order-modal',
                }}
            >
                {children}
            </Modal>
        </OrderModalProvider>
    );
};

/** Provider that only provides context and renders children (Modal prefab). */
export const OrderModalProviderComponent = ({ children, ...props }: Props) => (
    <OrderModalProvider {...props}>{children}</OrderModalProvider>
);

export {
    OrderModalWrapper as Wrapper,
    OrderModalProviderComponent as Provider,
    OrderModalTitle as Title,
    OrderModalInfo as Info,
    OrderModalDetails as Details,
    OrderModalBuyAgainButton as BuyAgainButton,
};

export { OrderModalWrapper as OrderModal };
